<script setup lang="ts">
  import CrossIcon from '@/icons/cross-icon.vue'
  import { useRdCodeStore } from '@/store/rd-code.store'
  import type { Product } from '@/types/product'
  import { setTouched } from '@/utils/product-utils'
  import { DropDown, FormInput, require, type Nullish } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, watch } from 'vue'

  const props = defineProps<{
    searchableProducts?: Product[]
    allProducts: Product[]
    errors?: Partial<Record<keyof Product, string>>
    placeholders?: Partial<Record<keyof Product, string>>
    selectedProducts: Product[]
    optionalFields: (keyof Product)[]
    disabledFields: (keyof Product | 'remove')[]
  }>()
  const product = defineModel<Product>({ required: true })
  const emit = defineEmits<{ (e: 'remove'): void }>()
  const data = computed(() => {
    return props.allProducts.filter(
      product => !props.selectedProducts.some(otherProduct => otherProduct.uuid === product.uuid)
    )
  })

  const touched = defineModel<Record<keyof Product, boolean>>('touched', {
    default: () => setTouched(false),
  })

  const rdCodeStore = useRdCodeStore()

  const searchableMap = computed(() => {
    return props.searchableProducts?.map(value => JSON.stringify(value).toLowerCase())
  })

  onMounted(() => {
    rdCodeStore.fetchRdCodes()
  })

  watch(
    () => props.allProducts,
    products => {
      if (products.length === 1 && products[0]) {
        Object.assign(product.value, products[0])
      }
    },
    { immediate: true }
  )

  function applyProductSearch(searchTerm: string) {
    const array = require(props.searchableProducts, `Search override can not be run when searchableProducts is undefined`)
    const map = require(searchableMap.value, 'Searhable map can not be undefined')
    const term = searchTerm.toLowerCase()

    return array.filter((value, index) => {
      if (props.selectedProducts.some(product => product.uuid == value.uuid)) return false

      if (map[index]?.includes(term)) {
        return value
      }
      return false
    })
  }

  function fieldError(field: keyof Product) {
    if (!touched.value[field]) return ''
    return props.errors == null ? '' : props.errors[field]
  }

  function copyFields(sourceProduct: Product | Nullish) {
    if (!sourceProduct || !product.value) return
    product.value = { ...sourceProduct }
  }
</script>

<template>
  <div class="products-extra-options-container">
    <div class="extra-option-container">
      <DropDown
        :error="fieldError('uuid')"
        :required="!optionalFields.includes('uuid')"
        :modelValue="product"
        :disabled="disabledFields?.includes('uuid')"
        :enableSearch="true"
        :searchOverride="searchableProducts ? applyProductSearch : null"
        :data="data"
        label="Product"
        @update:modelValue="copyFields"
        @blur="touched['uuid'] = true"
      >
        <template #item="{ item }">
          <template v-if="item.uuid == ''"> Selecteer product </template>
          <template v-else>
            <span v-if="item.naam" v-show="item.naam" class="item-name">{{ item.naam }}</span>
            <span v-else v-show="item.omschrijving" class="item-name">{{ item.omschrijving }}</span>
          </template>
        </template>
      </DropDown>
    </div>

    <div class="extra-option-container">
      <DropDown
        v-model="product.rdCode"
        :required="!optionalFields.includes('rdCode')"
        :data="rdCodeStore.rdCodes"
        :disabled="disabledFields?.includes('rdCode')"
        label="RD code"
        :error="fieldError('rdCode')"
        @blur="touched['rdCode'] = true"
      >
        <template #empty>RD code</template>
        <template #item="{ item }">
          <span class="item-name">
            {{ item.code }}
          </span>
        </template>
      </DropDown>
    </div>

    <div class="extra-option-container">
      <FormInput
        v-model="product.gewicht"
        label="Gewicht"
        :placeholder="placeholders?.['gewicht'] ?? 'gewicht'"
        type="number"
        :maxLength="32"
        @blur="touched['gewicht'] = true"
        :error="fieldError('gewicht')"
        :disabled="disabledFields.includes('gewicht')"
        :optional="optionalFields.includes('gewicht')"
      />
    </div>
    <button v-if="!disabledFields.includes('remove')" class="remove-button" @click="emit('remove')">
      <span><CrossIcon /></span>
    </button>
  </div>
</template>

<style scoped>
  .products-extra-options-container {
    display: flex;
    align-items: start;
    gap: var(--spacing-s);
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: 1px solid var(--colours-grey-3);
    border-radius: var(--components-form-field-corners);
    position: relative;
    margin-bottom: var(--spacing-s);

    p {
      flex: 1;
      margin: 0;
      color: #000;
    }

    .extra-option-container {
      width: 50%;
    }

    input[type='number'] {
      padding: 0.25rem;
      border: 1px solid var(--colours-grey-3);
      border-radius: var(--components-form-field-corners);
      text-align: center;
    }

    span {
      overflow: hidden;
      color: var(--colours-grey-4);
      text-overflow: ellipsis;
    }
    .item-name {
      color: var(--colours-grey-5);
    }

    .remove-button {
      background: none;
      border: none;
      color: red;
      padding: 0 var(--spacing-xs);
      align-self: start;

      span {
        color: red;
      }
    }
  }

  .product-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--spacing-s);
    padding: var(--spacing-xs);
    span {
      color: var(--colours-grey-4);
    }
  }
</style>
